import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Calendar, Mail } from "lucide-react";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";
import { ContactDialog } from "@/components/ContactDialog";

interface SeoPageLayoutProps {
  h1: string;
  intro?: string;
  heroImage?: string;
  children: React.ReactNode;
}

/**
 * Reusable layout for SEO content pages.
 * Logo above H1, brand-aligned styling (Or/Vert Forêt), CTA at bottom.
 */
export const SeoPageLayout: React.FC<SeoPageLayoutProps> = ({ h1, intro, heroImage, children }) => {
  const [contactOpen, setContactOpen] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src={brandLogo} alt={BRAND.name.full} className="w-9 sm:w-10 md:w-12 h-auto object-contain" />
            <div className="text-left">
              <div className="font-cinzel font-bold leading-tight">
                <span className="text-secondary text-sm sm:text-base md:text-lg">{BRAND.name.line1}</span>
                <span className="text-primary text-sm sm:text-base md:text-lg"> {BRAND.name.line2}</span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-montserrat uppercase tracking-wide">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/#domaines-accompagnement">
              <Button variant="ghost" size="sm" className="font-montserrat">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Retour</span>
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-primary font-montserrat">
                <Lock className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Espace Pro</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="py-6 sm:py-8 md:py-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <img
              src={brandLogo}
              alt={BRAND.name.full}
              className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 mx-auto object-contain"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-cinzel font-bold text-primary leading-tight tracking-wide">
              {h1}
            </h1>
            <div className="w-20 h-px bg-secondary mx-auto" />
            {intro && (
              <p className="text-base sm:text-lg md:text-xl text-foreground/80 font-montserrat">
                {intro}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <main className="py-8 sm:py-12 md:py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <article className="max-w-3xl mx-auto prose-content">
            {children}
          </article>
        </div>
      </main>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide">
              Envie d'en parler ensemble&nbsp;?
            </h2>
            <div className="w-16 h-px bg-secondary mx-auto" />
            <p className="text-base sm:text-lg text-foreground/80 font-montserrat">
              Si vous souhaitez en savoir plus ou démarrer un accompagnement, je suis à votre écoute.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-2">
              <Link to="/#accompagnements" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
                  <Calendar className="w-5 h-5 mr-2" />
                  Prendre rendez-vous
                </Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setContactOpen(true)}
                className="w-full sm:w-auto h-12 px-6 font-montserrat border-primary text-primary hover:bg-primary/10"
              >
                <Mail className="w-5 h-5 mr-2" />
                Échanger avec moi
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="py-6 border-t border-border bg-card">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground font-montserrat">
          © {new Date().getFullYear()} REGARD DE NATURE — Laura Elie, Naturopathie pour petits & grands
        </div>
      </footer>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};
